import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import {
  type gqlV2,
  type RequestQuery,
  useDashboard,
  useEnabledFeatures,
  useInfiniteQuery,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import { CrossCircleIcon, OfficeIcon, UserIcon } from '@vrplatform/icons';
import {
  parseAsActiveStatus,
  parseAsString,
  parseAsArrayOf,
  parseAsStringLiteral,
  useQueryStates,
  type HistoryOptions,
} from '@vrplatform/query-params';
import { hasValue, type Maybe } from '@vrplatform/utils';
import { Avatar, Center, Group } from '@mantine/core';
import {
  formatOwnerName,
  orderByContact,
  whereContacts,
} from '@vrplatform/ui-common';
import { useCallback, useMemo, useState } from 'react';
import { CustomFilter } from '../../../../components';
import {
  useTableStoreFilter,
  useTableStoreFilterType,
  useTableStoreSetAllPagesSelected,
  useTableStoreSetFilter,
  useTableStoreSetSelected,
} from '../../../../stores';
import { LoadingIndicator } from '@vrplatform/components-legacy';

type Issue = NonNullable<RequestQuery<'get:/listings'>['issue']>;

const ISSUES = [
  'all',
  'any',
  'missingOwnershipOrDeactivation',
  'missingFeesAndCommission',
  'missingOpeningBalance',
] satisfies Issue[];

export const useListingsTableFilter = () => {
  const [queryFilter, setQueryFilter] = useQueryStates({
    search: parseAsString,
    status: parseAsActiveStatus.withDefault('active'),
    collectionId: parseAsString,
    ownerIds: parseAsArrayOf(parseAsString),
    automationIds: parseAsArrayOf(parseAsString),
    pmsConnectionId: parseAsString,
    recurringFeeId: parseAsString,
    issue: parseAsStringLiteral(ISSUES),
  });

  const filterType = useTableStoreFilterType();
  const stateFilter = useTableStoreFilter();
  const setStateFilter = useTableStoreSetFilter();
  const setSelection = useTableStoreSetSelected();
  const setAllPagesSelected = useTableStoreSetAllPagesSelected();

  const filter = useMemo(() => {
    if (filterType === 'query-params') {
      return queryFilter;
    }
    return stateFilter as typeof queryFilter;
  }, [filterType, stateFilter, queryFilter]);

  const setFilter = useCallback(
    (filter: Partial<typeof stateFilter>, updateType?: HistoryOptions) => {
      setSelection({});
      setAllPagesSelected(false);

      if (filterType === 'query-params') {
        setQueryFilter(filter, { history: updateType || 'push' });
      } else {
        setStateFilter(filter);
      }
    },
    [
      filterType,
      setQueryFilter,
      setStateFilter,
      setSelection,
      setAllPagesSelected,
    ]
  );

  const reset = useCallback(() => {
    const initial: Record<keyof typeof queryFilter, null> = {
      search: null,
      status: null,
      ownerIds: null,
      automationIds: null,
      collectionId: null,
      pmsConnectionId: null,
      recurringFeeId: null,
      issue: null,
    };

    setStateFilter(initial);
    setQueryFilter(initial);
  }, [setQueryFilter, setStateFilter]);

  return {
    filter,
    setFilter,
    reset,
  };
};

export const ListingsTableFilter = ({
  hide,
}: {
  hide?: (
    | 'status'
    | 'owner'
    | 'automation'
    | 'pms-connection'
    | 'recurring-fee'
    | 'issue'
  )[];
}) => {
  const [dashboard] = useDashboard();
  const { GL_WITH_LEGACY_AUTOMATIONS } = useEnabledFeatures();

  return (
    <Group gap="xs">
      <SearchFilter />
      {dashboard !== 'owner' && (
        <>
          {!hide?.includes('status') && <Status />}
          {!hide?.includes('owner') && <OwnerFilter />}
          {!hide?.includes('recurring-fee') && <RecurringFee />}
          {!hide?.includes('issue') && <Issue />}
          {!hide?.includes('pms-connection') && <Connection />}
          {GL_WITH_LEGACY_AUTOMATIONS && <AFilter />}
        </>
      )}
    </Group>
  );
};

const SearchFilter = () => {
  const { filter, setFilter } = useListingsTableFilter();

  return (
    <Filter.Search
      value={filter.search || ''}
      setValue={(v) => setFilter({ search: v || null })}
    />
  );
};

const Status = () => {
  const { filter, setFilter } = useListingsTableFilter();

  return (
    <Filter.Status
      value={filter.status}
      setValue={(v) => v && setFilter({ status: v })}
      inactiveLabel="Deactivated"
    />
  );
};

const ISSUE_OPTIONS: SelectItem<Issue>[] = [
  {
    label: 'No Owner Assigned',
    value: 'missingOwnershipOrDeactivation',
  },
  {
    label: 'Missing Fees and Commission',
    value: 'missingFeesAndCommission',
  },
  {
    label: 'Missing Opening Balance',
    value: 'missingOpeningBalance',
  },
];

const Issue = () => {
  const { filter, setFilter } = useListingsTableFilter();

  const value = useMemo(() => {
    return ISSUE_OPTIONS.find((x) => x.value === filter.issue) || null;
  }, [filter.issue]);

  return (
    <Filter.Select
      data={{
        options: ISSUE_OPTIONS,
      }}
      value={value}
      type="single"
      setValue={(v) => setFilter({ issue: v?.value || null })}
      label="Issue"
    />
  );
};

const OwnerFilter = () => {
  const { filter, setFilter } = useListingsTableFilter();
  const [{ id: teamId }] = useTeam();

  const [search, setSearch] = useState('');
  const gate = useSelectQueryGate({ value: filter.ownerIds });

  const queryData = useInfiniteQuery(
    (q, { teamId, search }, { limit, offset }) => {
      const where = whereContacts({
        tenantId: teamId,
        search,
        type: 'owner',
      });

      const list = q
        .contacts({
          limit,
          offset,
          order_by: orderByContact,
          where,
        })
        .map<SelectItem>((contact) => ({
          label: formatOwnerName(contact),
          value: contact.id,
          group: contact.companyType ? 'Company' : 'Individual',
          icon: contact.companyType ? (
            <OfficeIcon size={16} />
          ) : (
            <UserIcon size={16} />
          ),
        }));

      const aggregate = q.contactAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchOptions,
      queryKey: 'owners',
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );

  const { data } = useQuery(
    (q, { ownerIds }) => {
      if (ownerIds.includes('unmapped')) {
        return {
          value: [
            {
              label: 'Unassigned',
              value: 'unmapped',
            },
          ],
        };
      }

      if (ownerIds.length === 0) {
        return {
          value: [],
        };
      }

      const value = q
        .contacts({
          where: {
            id: { _in: ownerIds },
          },
        })
        .map<SelectItem>((contact) => ({
          label: formatOwnerName(contact),
          value: contact.id,
        }));

      return {
        value,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryKey: 'owners',
      variables: {
        ownerIds: (filter.ownerIds || []).filter(hasValue),
      },
    }
  );

  const value = gate.hasValue ? data?.value || [] : [];

  return (
    <Filter.Select
      value={value}
      setValue={(v) =>
        setFilter({ ownerIds: v.length ? v.map((i) => i.value) : null })
      }
      label="Owner"
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
      customBottomActions={[
        {
          id: 'unmapped',
          label: 'Unassigned',
          icon: (
            <Center>
              <CrossCircleIcon size={14} />
            </Center>
          ),
          onSubmit: () => {
            setFilter({ ownerIds: ['unmapped'] });
          },
        },
      ]}
    />
  );
};

const _Collection = () => {
  const [{ id: teamId }] = useTeam();
  const { filter, setFilter } = useListingsTableFilter();
  const gate = useSelectQueryGate({ value: filter.collectionId });

  const queryData = useQuery(
    (q, { teamId }) => {
      const where: gqlV2.listing_collection_bool_exp = {
        tenantId: { _eq: teamId },
        listings: {},
      };

      const list = q
        .listingCollections({
          where,
          order_by: [
            {
              name: 'asc',
            },
          ],
        })
        .map<SelectItem>((collection) => ({
          label: collection.name || 'No name',
          value: collection.id,
        }));

      return {
        list,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchAny,
      queryKey: 'listings',
      variables: {
        teamId,
      },
    }
  );

  const unmappedItem: SelectItem = {
    label: 'Ungrouped',
    value: 'null',
  };

  const value = useMemo(() => {
    if (!gate.hasValue) return null;
    if (filter.collectionId === 'null') return unmappedItem;

    return (
      queryData.data?.list.find((x) => x.value === filter.collectionId) || null
    );
  }, [gate.hasValue, queryData.data?.list, filter.collectionId]);

  return (
    <Filter.Select
      type="single"
      value={value}
      setValue={(v) => setFilter({ collectionId: v?.value || null })}
      label="Listing Group"
      withinPortal
      onOpenChange={gate.onOpenChange}
      data={{
        options: queryData.data?.list || [],
        error: queryData.error,
        loading: queryData.isLoading,
      }}
      pinnedItems={[unmappedItem]}
    />
  );
};

const AFilter = () => {
  const { filter, setFilter } = useListingsTableFilter();
  return (
    <CustomFilter.Automation
      automationIds={filter.automationIds}
      setAutomationIds={(ids) => setFilter({ automationIds: ids })}
    />
  );
};

const RecurringFee = () => {
  const { filter, setFilter } = useListingsTableFilter();

  return (
    <CustomFilter.RecurringFee
      recurringFeeId={filter.recurringFeeId}
      setRecurringFeeId={(v) => setFilter({ recurringFeeId: v || null })}
    />
  );
};

const getConnectionBool = ({
  search,
  teamId,
}: {
  teamId: string;
  search: Maybe<string>;
}): gqlV2.connection_bool_exp => ({
  tenantId: { _eq: teamId },
  app: {
    category: { _eq: 'propertyManagementSystem' },
    name: search ? { _ilike: `%${search}%` } : undefined,
  },
});

const Connection = () => {
  const [{ id: teamId }] = useTeam();
  const { filter, setFilter } = useListingsTableFilter();
  const gate = useSelectQueryGate({ value: filter.pmsConnectionId });

  const [search, setSearch] = useState('');

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      const where: gqlV2.connection_bool_exp = getConnectionBool(args);

      const aggregate =
        q.connectionAggregate({ where }).aggregate?.count() || 0;

      const list = q
        .connection({
          where,
          limit,
          offset,
          order_by: [{ name: 'asc_nulls_last' }],
        })
        .map<SelectItem>((connection) => ({
          label: connection.name || 'No name',
          value: connection.id,
          icon: connection.app.iconRound ? (
            <Avatar size={16} mt={3} src={connection.app.iconRound} />
          ) : undefined,
        }));

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchOptions,
      queryKey: 'listings',
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );

  const { data: hasMultiplePms, isInitialLoading: loadingMultiplePms } =
    useQuery(
      (q, args) => {
        if (args.pmsConnectionId) {
          return {
            show: true,
          };
        }

        const cons = q
          .connection({
            where: getConnectionBool({
              search: null,
              teamId: args.teamId,
            }),
            limit: 2,
          })
          .map((connection) => connection.id);

        return {
          show: cons.length > 1,
        };
      },
      {
        skip: !teamId,
        queryKey: 'connections',
        keepPreviousData: true,
        variables: {
          teamId,
          pmsConnectionId: filter.pmsConnectionId,
        },
      }
    );

  const { data, isInitialLoading: loadingValue } = useQuery(
    (q, { pmsConnectionId }) => {
      if (!pmsConnectionId) return { value: null };

      const value = q
        .connection({
          where: {
            id: { _eq: pmsConnectionId },
          },
        })
        .map<SelectItem>((connection) => ({
          label: connection.name || 'No name',
          value: connection.id,
          icon: connection.app.iconRound ? (
            <Avatar size={16} mt={3} src={connection.app.iconRound} />
          ) : undefined,
        }))[0];

      return {
        value,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryKey: 'connections',
      keepPreviousData: true,
      variables: {
        pmsConnectionId: filter.pmsConnectionId,
        teamId,
      },
    }
  );

  if ((loadingValue && gate.hasValue) || loadingMultiplePms)
    return <LoadingIndicator size="xs" />;

  if (!hasMultiplePms?.show) return null;

  return (
    <Filter.Select
      type="single"
      value={gate.hasValue ? data?.value || null : null}
      setValue={(v) => setFilter({ pmsConnectionId: v?.value || null })}
      label="Connection"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
      hideIcon
    />
  );
};
